import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import DOMPurify from "dompurify";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Clock,
  MapPin,
  Check,
  X,
  Calendar,
  ArrowRight,
  Phone,
  MessageCircle,
  Compass,
} from "lucide-react";
import axios from "axios";
import logger from '@/utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ThemeTourDetailPage = () => {
  const { id } = useParams();
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTour = async () => {
      try {
        const response = await axios.get(`${API}/theme-tours/${id}`);
        setTour(response.data);
      } catch (e) {
        logger.error("Error fetching theme tour:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchTour();
  }, [id]);

  const defaultExclusions = ["Personal expenses", "Travel insurance", "Tips & gratuities", "Optional activities", "Visa fees (if applicable)"];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-slate-600">Loading theme tour details...</div>
      </div>
    );
  }

  if (!tour) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-heading text-primary mb-4">Theme Tour Not Found</h2>
          <Link to="/theme-tours">
            <Button>View All Theme Tours</Button>
          </Link>
        </div>
      </div>
    );
  }

  const itinerary = tour.itinerary && tour.itinerary.length > 0 ? tour.itinerary : [];
  const exclusions = tour.exclusions && tour.exclusions.length > 0 ? tour.exclusions : defaultExclusions;

  return (
    <div data-testid="theme-tour-detail-page">
      {/* Hero */}
      <section className="relative pt-24 pb-16 bg-slate-900" data-testid="tour-hero">
        <div className="absolute inset-0">
          <img
            src={tour.image}
            alt={tour.title}
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/70 to-slate-900/50" />
        </div>
        <div className="container-custom relative z-10 pt-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex flex-wrap gap-3 mb-4">
              <span className="px-4 py-1 bg-coral text-white text-sm font-medium rounded-full uppercase">
                {tour.category}
              </span>
              <span className="px-4 py-1 bg-purple-500 text-white text-sm font-medium rounded-full capitalize flex items-center gap-1">
                <Compass className="h-3 w-3" />
                {tour.theme}
              </span>
            </div>
            <h1 className="heading-1 text-white mb-4">{tour.title}</h1>
            <div className="flex flex-wrap items-center gap-6 text-white/80">
              <span className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                {tour.destination}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                {tour.duration}
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Available Year Round
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding bg-white" data-testid="tour-content">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Overview */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="heading-3 text-primary mb-6">Tour Overview</h2>
                <p className="body-lg">
                  Embark on an unforgettable {tour.theme} experience in {tour.destination} with our carefully curated {tour.title} tour. 
                  This {tour.duration} journey combines the best of {tour.theme} activities with comfortable accommodations and expert guidance, 
                  creating memories that will last a lifetime.
                </p>
              </motion.div>

              {/* Highlights */}
              {tour.highlights && tour.highlights.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                >
                  <h2 className="heading-3 text-primary mb-6">Tour Highlights</h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {tour.highlights.map((highlight, index) => (
                      <div
                        key={`highlight-${highlight.slice(0, 20)}-${index}`}
                        className="flex items-center gap-3 p-4 bg-accent rounded-lg"
                        data-testid={`highlight-${index}`}
                      >
                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Check className="h-4 w-4 text-purple-600" />
                        </div>
                        <span className="font-medium text-primary">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Itinerary */}
              {itinerary.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <h2 className="heading-3 text-primary mb-6">Day-wise Itinerary</h2>
                  <div className="space-y-4">
                    {itinerary.map((day, index) => (
                      <div
                        key={`itin-day-${day.day}`}
                        className="p-5 bg-accent rounded-xl border border-slate-100"
                        data-testid={`itinerary-day-${day.day}`}
                      >
                        <div className="flex gap-4">
                          <div className="w-14 h-14 bg-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                            <div className="text-center">
                              <span className="text-white text-xs block">Day</span>
                              <span className="text-white font-bold text-lg">{day.day}</span>
                            </div>
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-primary text-lg mb-2">{day.title}</h4>
                            {day.description && (
                              <div 
                                className="text-slate-600 mb-3 prose prose-sm max-w-none"
                                dangerouslySetInnerHTML={{ 
                                  __html: DOMPurify.sanitize(day.description
                                    .replace(/\n/g, '<br/>')
                                    .replace(/•\s*/g, '<br/>• ')
                                    .replace(/^<br\/>/, ''))
                                }}
                              />
                            )}
                            {day.activities && day.activities.length > 0 && (
                              <div className="flex flex-wrap gap-2">
                                {day.activities.map((activity, actIdx) => (
                                  <span
                                    key={actIdx}
                                    className="px-3 py-1 bg-white rounded-full text-sm text-slate-700 border border-slate-200"
                                  >
                                    {activity}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Inclusions / Exclusions */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="grid sm:grid-cols-2 gap-8"
              >
                <div>
                  <h3 className="heading-3 text-primary mb-4">Inclusions</h3>
                  <ul className="space-y-3">
                    {tour.inclusions?.map((item, index) => (
                      <li key={`inc-${item.slice(0, 20)}-${index}`} className="flex items-center gap-3 text-slate-600" data-testid={`inclusion-${index}`}>
                        <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="heading-3 text-primary mb-4">Exclusions</h3>
                  <ul className="space-y-3">
                    {exclusions.map((item, index) => (
                      <li key={`exc-${item.slice(0, 20)}-${index}`} className="flex items-center gap-3 text-slate-600" data-testid={`exclusion-${index}`}>
                        <X className="h-5 w-5 text-red-500 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>

              {/* Terms & Conditions */}
              {tour.terms_conditions && tour.terms_conditions.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  <h3 className="heading-3 text-primary mb-4">Terms & Conditions</h3>
                  <ul className="space-y-2 text-slate-600 text-sm">
                    {tour.terms_conditions
                      .filter(term => term && term.trim() && !term.toLowerCase().includes('trip terms & conditions'))
                      .map((term, index) => (
                        <li key={`term-${term.slice(0, 20)}-${index}`} className="flex items-start gap-2">
                          <span className="text-coral font-bold">•</span>
                          {term.replace(/^[•\-\*]\s*/, '').trim()}
                        </li>
                    ))}
                  </ul>
                </motion.div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-28">
                <Card className="shadow-xl" data-testid="booking-card">
                  <CardHeader className="bg-gradient-to-r from-purple-600 to-coral text-white rounded-t-xl">
                    <CardTitle className="text-center">
                      <span className="text-sm font-normal block mb-1">Starting from</span>
                      <span className="text-4xl font-bold">₹{tour.price?.toLocaleString()}</span>
                      <span className="text-sm font-normal block mt-1">per person</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6 space-y-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Duration</span>
                        <span className="font-medium">{tour.duration}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Tour Code</span>
                        <span className="font-mono font-medium text-purple-600">#{tour.tour_code}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Theme</span>
                        <span className="font-medium capitalize">{tour.theme}</span>
                      </div>
                    </div>

                    <Link to={`/get-quote?tour=${tour.title}&destination=${tour.destination}&theme=${tour.theme}`} className="block">
                      <Button className="btn-coral w-full" data-testid="enquire-now-btn">
                        Enquire Now
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </Link>

                    <div className="flex gap-3">
                      <a href="tel:+918369033791" className="flex-1">
                        <Button variant="outline" className="w-full" data-testid="call-btn">
                          <Phone className="mr-2 h-4 w-4" />
                          Call
                        </Button>
                      </a>
                      <a
                        href={`https://wa.me/918369033791?text=Hi, I'm interested in the ${tour.title} theme tour (Code: ${tour.tour_code})`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1"
                      >
                        <Button variant="outline" className="w-full border-green-500 text-green-600 hover:bg-green-50" data-testid="whatsapp-btn">
                          <MessageCircle className="mr-2 h-4 w-4" />
                          WhatsApp
                        </Button>
                      </a>
                    </div>

                    <p className="text-xs text-center text-slate-500">
                      Free consultation • No booking fees
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ThemeTourDetailPage;
